import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Modal } from './Modal';
import { Button } from '../atoms';
import type { EpisodeDetailResponse } from '../../types/api/episode';
import type {
  CompleteMultipartUploadRequest,
  InitiateMultipartUploadRequest,
  UploadedPartStatus,
  VideoMultipartStatusResponse,
  VideoVariant,
} from '../../types/api/movie';
import { useEpisodeActions } from '../../hooks/useEpisodes';
import {
  clearEpisodeUploadSession,
  getEpisodeUploadSession,
  saveEpisodeUploadSession,
  type EpisodeUploadSession,
} from '../../utils/uploadSessionStorage';
import toast from 'react-hot-toast';

const DEFAULT_VIDEO_CHUNK_SIZE = 8 * 1024 * 1024; // 8MB per chunk
const VIDEO_MIN_PART_SIZE = 5 * 1024 * 1024; // Minimum part size (except last part)

const VIDEO_QUALITY_OPTIONS = [
  { value: 'ULTRA_HD_4K', label: 'Ultra HD (4K)' },
  { value: 'FULL_HD_1080P', label: 'Full HD (1080p)' },
  { value: 'HD_720P', label: 'HD (720p)' },
  { value: 'SD_480P', label: 'SD (480p)' },
  { value: 'SD_360P', label: 'SD (360p)' },
  { value: 'SD_240P', label: 'SD (240p)' },
];

const VIDEO_TYPE_OPTIONS = [
  { value: 'EPISODE_CONTENT', label: 'Episode Content' },
  { value: 'FULL_MOVIE', label: 'Full Video' },
  { value: 'TRAILER', label: 'Trailer' },
  { value: 'TEASER', label: 'Teaser' },
  { value: 'CLIP', label: 'Clip' },
  { value: 'EXTRA', label: 'Extra' },
];

const getAdaptiveChunkSize = (fileSize: number): number => {
  const sizeInGB = fileSize / (1024 * 1024 * 1024);

  if (sizeInGB >= 20) {
    return 256 * 1024 * 1024; // 256 MB
  }
  if (sizeInGB >= 10) {
    return 192 * 1024 * 1024; // 192 MB
  }
  if (sizeInGB >= 5) {
    return 128 * 1024 * 1024; // 128 MB
  }
  if (sizeInGB >= 2) {
    return 96 * 1024 * 1024; // 96 MB
  }
  if (sizeInGB >= 1) {
    return 64 * 1024 * 1024; // 64 MB
  }
  if (sizeInGB >= 0.5) {
    return 32 * 1024 * 1024; // 32 MB
  }
  if (sizeInGB >= 0.25) {
    return 16 * 1024 * 1024; // 16 MB
  }

  return DEFAULT_VIDEO_CHUNK_SIZE;
};

export interface EpisodeVideoUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  dramaId: string;
  episodeId: string;
  episodeTitle?: string;
}

type UploadStatus = 'idle' | 'initiating' | 'uploading' | 'completing' | 'completed';

export const EpisodeVideoUploadModal: React.FC<EpisodeVideoUploadModalProps> = ({
  isOpen,
  onClose,
  dramaId,
  episodeId,
  episodeTitle,
}) => {
  const {
    getEpisode,
    initiateVideoUpload,
    completeVideoUpload,
    getMultipartUploadStatus,
    regenerateMultipartPartUrls,
    deleteVideo,
  } = useEpisodeActions(dramaId);

  const [episodeDetails, setEpisodeDetails] = useState<EpisodeDetailResponse | null>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [videoFile, setVideoFile] = useState<File | null>(null);
  const [videoQuality, setVideoQuality] = useState<string>('FULL_HD_1080P');
  const [videoType, setVideoType] = useState<string>('EPISODE_CONTENT');
  const [makeDefaultVideo, setMakeDefaultVideo] = useState<boolean>(true);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const uploadSessionRef = useRef<{ key: string; uploadId: string } | null>(null);

  const [isDeletingVideo, setIsDeletingVideo] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [, setSelectedVideoVariant] = useState<VideoVariant | null>(null);

  const normaliseEtag = (raw: string | null): string => {
    if (!raw) {
      throw new Error('Missing ETag value');
    }
    return raw.replace(/^W\//, '').replace(/^"+|"+$/g, '');
  };

  const buildUploadedPartMap = (uploadedParts: UploadedPartStatus[] | null | undefined) => {
    const map = new Map<number, { eTag: string; size: number }>();
    if (!uploadedParts || uploadedParts.length === 0) {
      return map;
    }

    uploadedParts.forEach((part) => {
      try {
        const eTag = normaliseEtag(part.eTag);
        map.set(part.partNumber, {
          eTag,
          size: part.size ?? 0,
        });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to normalise ETag from status response:', error);
      }
    });

    return map;
  };

  const getMissingPartNumbers = (
    totalPartCount: number,
    uploadedPartMap: Map<number, { eTag: string; size: number }>
  ): number[] => {
    const missing: number[] = [];
    for (let partNumber = 1; partNumber <= totalPartCount; partNumber++) {
      if (!uploadedPartMap.has(partNumber)) {
        missing.push(partNumber);
      }
    }
    return missing;
  };

  const uploadPartWithRetry = async (
    url: string,
    chunk: Blob,
    partNumber: number,
    maxRetries: number = 3
  ): Promise<string> => {
    let lastError: unknown;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/octet-stream',
          },
          body: chunk,
        });

        if (!response.ok) {
          // Treat 403 as potentially expired URL so caller can regenerate
          if (response.status === 403) {
            const error = new Error('URL_EXPIRED');
            (error as any).status = response.status;
            throw error;
          }

          throw new Error(
            `Upload failed for part ${partNumber} (status ${response.status})`
          );
        }

        const rawETag = response.headers.get('ETag') || response.headers.get('etag');
        const eTag = normaliseEtag(rawETag);
        return eTag;
      } catch (error: any) {
        lastError = error;

        if (error?.message === 'URL_EXPIRED') {
          throw error;
        }

        if (attempt < maxRetries) {
          const delay = Math.min(1000 * Math.pow(2, attempt - 1), 8000);
          // eslint-disable-next-line no-console
          console.warn(
            `Part ${partNumber} attempt ${attempt} failed, retrying in ${delay}ms...`,
            error
          );
          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
    }

    throw lastError instanceof Error
      ? lastError
      : new Error(`Part ${partNumber} failed after ${maxRetries} attempts`);
  };

  const resetVideoUploadState = () => {
    setVideoFile(null);
    setUploadProgress(0);
    setUploadStatus('idle');
    setUploadError(null);
    uploadSessionRef.current = null;
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleVideoFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      resetVideoUploadState();
      return;
    }

    if (file.type && !file.type.startsWith('video/')) {
      setUploadError('Please select a valid video file');
      resetVideoUploadState();
      return;
    }

    if (file.size === 0) {
      setUploadError('Selected file is empty');
      resetVideoUploadState();
      return;
    }

    setVideoFile(file);
    setUploadError(null);
    setUploadProgress(0);
    setUploadStatus('idle');
  };

  const loadEpisodeDetails = useCallback(async () => {
    if (!dramaId || !episodeId) {
      return;
    }

    try {
      setIsLoadingDetails(true);
      setLoadError(null);
      const details = await getEpisode(episodeId);
      setEpisodeDetails(details);
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Failed to load episode details:', error);
      setLoadError(error.message || 'Failed to load episode details');
      toast.error(error.message || 'Failed to load episode details');
    } finally {
      setIsLoadingDetails(false);
    }
  }, [dramaId, episodeId, getEpisode]);

  useEffect(() => {
    if (isOpen) {
      loadEpisodeDetails();
      setUploadError(null);
    } else {
      resetVideoUploadState();
      setEpisodeDetails(null);
      setLoadError(null);
    }
  }, [isOpen, loadEpisodeDetails]);

  const getVideoFileId = (variant: VideoVariant): string | null => {
    return variant.videoId || (variant as any).fileId || (variant as any).key || null;
  };

  const handleDeleteVideo = async (variant: VideoVariant) => {
    const fileId = getVideoFileId(variant);
    if (!fileId) {
      toast.error('Cannot delete video: Missing video identifier');
      return;
    }

    setSelectedVideoVariant(variant);
    setIsDeletingVideo(true);
    try {
      await deleteVideo(episodeId, fileId);
      toast.success('Video deleted successfully');

      const updatedDetails = await getEpisode(episodeId);
      setEpisodeDetails(updatedDetails);
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Failed to delete episode video:', error);
      toast.error(error.message || 'Failed to delete video');
    } finally {
      setIsDeletingVideo(false);
      setSelectedVideoVariant(null);
    }
  };

  const handleVideoUpload = async () => {
    if (!dramaId || !episodeId) {
      toast.error('Episode context is missing');
      return;
    }

    if (!videoFile) {
      setUploadError('Please select a video file to upload');
      return;
    }

    const fileSize = videoFile.size;
    const adaptiveChunkSize = Math.max(getAdaptiveChunkSize(fileSize), VIDEO_MIN_PART_SIZE);
    const estimatedPartCount = Math.max(1, Math.ceil(fileSize / adaptiveChunkSize));

    setIsUploadingVideo(true);
    setUploadStatus('initiating');
    setUploadProgress(0);
    setUploadError(null);

    let completedParts: { partNumber: number; eTag: string }[] = [];
    let sessionInfo: { key: string; uploadId: string } | null = null;
    let session: EpisodeUploadSession | null = null;

    try {
      // Try to resume existing upload session if it matches the selected file
      session = getEpisodeUploadSession(dramaId, episodeId) || null;
      const canResumeFromSession =
        session &&
        session.filename === videoFile.name &&
        session.fileSize === fileSize &&
        session.dramaId === dramaId &&
        session.episodeId === episodeId;

      if (canResumeFromSession && session) {
        sessionInfo = {
          key: session.key,
          uploadId: session.uploadId,
        };
        uploadSessionRef.current = sessionInfo;
      } else {
        // Start a new multipart upload session
        const initiatePayload: InitiateMultipartUploadRequest = {
          filename: videoFile.name,
          contentType: videoFile.type || 'application/octet-stream',
          partCount: estimatedPartCount,
          quality: videoQuality,
          videoType,
          makeDefault: makeDefaultVideo,
        };

        const initiateResponse = await initiateVideoUpload(episodeId, initiatePayload);

        sessionInfo = {
          key: initiateResponse.key,
          uploadId: initiateResponse.uploadId,
        };
        uploadSessionRef.current = sessionInfo;

        session = {
          dramaId,
          episodeId,
          contentId: `${dramaId}:${episodeId}`,
          key: initiateResponse.key,
          uploadId: initiateResponse.uploadId,
          partCount: estimatedPartCount,
          filename: videoFile.name,
          fileSize,
          chunkSize: adaptiveChunkSize,
          quality: videoQuality,
          videoType,
          makeDefault: makeDefaultVideo,
          contentType: videoFile.type || 'application/octet-stream',
          createdAt: Date.now(),
        };

        saveEpisodeUploadSession(session);
      }

      if (!sessionInfo || !session) {
        throw new Error('Failed to initialise upload session');
      }

      setUploadStatus('uploading');

      // Check which parts are already uploaded (for resume scenarios)
      let uploadedBytes = 0;
      let uploadedPartMap = new Map<number, { eTag: string; size: number }>();
      let totalPartCount = session.partCount;

      try {
        const status: VideoMultipartStatusResponse = await getMultipartUploadStatus(
          episodeId,
          sessionInfo.key,
          sessionInfo.uploadId
        );

        uploadedPartMap = buildUploadedPartMap(status.uploadedParts);
        if (status.partCount != null && status.partCount > 0) {
          totalPartCount = status.partCount;
        }

        status.uploadedParts?.forEach((part) => {
          uploadedBytes += part.size ?? 0;
        });
      } catch (statusError) {
        // eslint-disable-next-line no-console
        console.warn(
          'Failed to fetch multipart upload status, assuming fresh upload',
          statusError
        );
        uploadedBytes = 0;
        uploadedPartMap = new Map();
        totalPartCount = session.partCount;
      }

      completedParts = Array.from(uploadedPartMap.entries()).map(
        ([partNumber, value]) => ({
          partNumber,
          eTag: value.eTag,
        })
      );

      // Determine which parts still need to be uploaded
      const missingPartNumbers = getMissingPartNumbers(totalPartCount, uploadedPartMap);

      if (missingPartNumbers.length === 0) {
        // All parts are already uploaded; just complete the multipart upload
        setUploadProgress(Math.min(100, Math.round((uploadedBytes / fileSize) * 100)));
      } else {
        // Regenerate presigned URLs for all missing parts
        const presignResponse = await regenerateMultipartPartUrls(episodeId, {
          key: sessionInfo.key,
          uploadId: sessionInfo.uploadId,
          partNumbers: missingPartNumbers,
        });

        const partsToUpload = [...presignResponse.parts].sort(
          (a, b) => a.partNumber - b.partNumber
        );

        const baseChunkSize = session.chunkSize || adaptiveChunkSize;

        for (let index = 0; index < partsToUpload.length; index++) {
          const part = partsToUpload[index];

          if (uploadedBytes >= fileSize) {
            break;
          }

          const remainingBytes = fileSize - uploadedBytes;
          const partsRemaining = partsToUpload.length - index;
          const isLastPart = partsRemaining === 1;
          const minPartSize = part.minPartSize ?? VIDEO_MIN_PART_SIZE;
          const maxPartSize = part.maxPartSize ?? Math.max(baseChunkSize, minPartSize);

          // Calculate an appropriate chunk size ensuring non-final parts stay >= 5MB
          const bytesNeededForRemainingParts = isLastPart
            ? 0
            : Math.max(VIDEO_MIN_PART_SIZE, minPartSize) * (partsRemaining - 1);

          if (!isLastPart && remainingBytes - bytesNeededForRemainingParts < minPartSize) {
            throw new Error(
              `Not enough data remaining to satisfy minimum part size for part ${part.partNumber}`
            );
          }

          const chunkSize = isLastPart
            ? remainingBytes
            : Math.min(
                Math.max(
                  minPartSize,
                  Math.min(baseChunkSize, remainingBytes - bytesNeededForRemainingParts)
                ),
                maxPartSize,
                remainingBytes
              );

          const chunk = videoFile.slice(uploadedBytes, uploadedBytes + chunkSize);

          if (!isLastPart && chunk.size < minPartSize) {
            throw new Error(
              `Uploaded chunk ${part.partNumber} (${chunk.size} bytes) is smaller than the minimum ${minPartSize} bytes`
            );
          }

          try {
            const eTag = await uploadPartWithRetry(part.url, chunk, part.partNumber);
            completedParts.push({
              partNumber: part.partNumber,
              eTag,
            });
            uploadedBytes += chunk.size;
            setUploadProgress(
              Math.min(100, Math.round((uploadedBytes / fileSize) * 100))
            );
          } catch (error: any) {
            if (error?.message === 'URL_EXPIRED') {
              const singlePresign = await regenerateMultipartPartUrls(episodeId, {
                key: sessionInfo.key,
                uploadId: sessionInfo.uploadId,
                partNumbers: [part.partNumber],
              });

              const refreshedPart = singlePresign.parts[0];
              const eTag = await uploadPartWithRetry(
                refreshedPart.url,
                chunk,
                refreshedPart.partNumber
              );

              completedParts.push({
                partNumber: refreshedPart.partNumber,
                eTag,
              });
              uploadedBytes += chunk.size;
              setUploadProgress(
                Math.min(100, Math.round((uploadedBytes / fileSize) * 100))
              );
            } else {
              throw error;
            }
          }
        }
      }

      if (completedParts.length === 0) {
        throw new Error('No video parts were uploaded');
      }

      // Ensure parts are ordered by partNumber for completion
      completedParts.sort((a, b) => a.partNumber - b.partNumber);

      setUploadStatus('completing');

      const completePayload: CompleteMultipartUploadRequest = {
        key: sessionInfo.key,
        uploadId: sessionInfo.uploadId,
        parts: completedParts,
        quality: videoQuality,
        videoType,
        makeDefault: makeDefaultVideo,
        durationSeconds: null,
        bitrate: null,
        codec: null,
        container: null,
        width: null,
        height: null,
      };

      await completeVideoUpload(episodeId, completePayload);

      setUploadStatus('completed');
      setUploadProgress(100);
      toast.success('Episode video uploaded successfully');

      const updatedDetails = await getEpisode(episodeId);
      setEpisodeDetails(updatedDetails);

      clearEpisodeUploadSession(dramaId, episodeId);
      resetVideoUploadState();
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Episode video upload failed:', error);
      const message = error?.message || 'Failed to upload video';
      setUploadError(message);
      toast.error(message);
      // Do not abort the multipart upload here so that it can be resumed later.
    } finally {
      setIsUploadingVideo(false);
      setUploadStatus('idle');
      uploadSessionRef.current = null;
    }
  };

  const handleClose = () => {
    if (isUploadingVideo) {
      toast.error('Please wait for the upload to finish or fail before closing.');
      return;
    }
    resetVideoUploadState();
    setEpisodeDetails(null);
    setLoadError(null);
    onClose();
  };

  const renderVideoVariantsTable = () => {
    if (!episodeDetails?.videoVariants || episodeDetails.videoVariants.length === 0) {
      return (
        <p className="text-sm text-gray-500 text-left">
          No videos uploaded for this episode yet.
        </p>
      );
    }

    return (
      <div className="mt-4 border border-gray-200 rounded-lg overflow-hidden">
        <div className="max-h-96 overflow-y-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-2 text-left font-semibold text-gray-700 uppercase tracking-wide text-xs">
                  Type
                </th>
                <th className="px-4 py-2 text-left font-semibold text-gray-700 uppercase tracking-wide text-xs">
                  Quality
                </th>
                <th className="px-4 py-2 text-left font-semibold text-gray-700 uppercase tracking-wide text-xs">
                  Status
                </th>
                <th className="px-4 py-2 text-left font-semibold text-gray-700 uppercase tracking-wide text-xs">
                  Default
                </th>
                <th className="px-4 py-2 text-left font-semibold text-gray-700 uppercase tracking-wide text-xs">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {episodeDetails.videoVariants.map((variant: VideoVariant) => {
                const variantKey =
                  (variant as any).fileId ||
                  (variant as any).videoId ||
                  (variant as any).key ||
                  `${variant.videoType}-${variant.quality}`;

                const statusClasses =
                  variant.status === 'READY'
                    ? 'bg-green-100 text-green-800'
                    : variant.status === 'PROCESSING' || variant.status === 'UPLOADING'
                    ? 'bg-yellow-100 text-yellow-800'
                    : variant.status === 'FAILED'
                    ? 'bg-red-100 text-red-800'
                    : 'bg-gray-100 text-gray-800';

                return (
                  <tr key={variantKey}>
                    <td className="px-4 py-3 text-gray-800">{variant.videoType || '-'}</td>
                    <td className="px-4 py-3 text-gray-800">{variant.quality || '-'}</td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${statusClasses}`}
                      >
                        {variant.status || 'UNKNOWN'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-800">
                      {variant.isDefault ? (
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-semibold bg-blue-100 text-blue-800 rounded-full">
                          Default
                        </span>
                      ) : (
                        <span className="text-xs text-gray-500">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <Button
                        type="button"
                        variant="danger"
                        size="sm"
                        onClick={() => handleDeleteVideo(variant)}
                        disabled={isDeletingVideo}
                        ariaLabel={`Delete ${variant.videoType || 'video'} - ${
                          variant.quality || 'unknown quality'
                        }`}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Episode Video Upload" size="xl">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="text-left">
            <h2 className="text-base font-semibold text-gray-900">
              {episodeTitle || 'Episode'}
            </h2>
            <p className="text-sm text-gray-600">
              Upload and manage videos for this episode.
            </p>
          </div>
        </div>

        {isLoadingDetails && (
          <p className="text-sm text-gray-500 text-left">Loading episode details...</p>
        )}
        {loadError && (
          <p className="text-sm text-red-600 text-left">{loadError}</p>
        )}

        <div className="space-y-4 pt-2 border-t border-dashed border-gray-200">
          <h3 className="text-sm font-semibold text-gray-900 text-left">
            Upload New Video
          </h3>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="space-y-3">
              <label className="block text-sm font-medium text-left text-gray-700">
                Video File <span className="text-red-500">*</span>
              </label>
              <input
                ref={fileInputRef}
                type="file"
                accept="video/*"
                onChange={handleVideoFileChange}
                disabled={isUploadingVideo}
                className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
              />
              <p className="text-xs text-gray-500 text-left">
                Supported: MP4, MOV, MKV, WEBM and other video formats. Minimum chunk size
                5MB per part.
              </p>
              {videoFile && (
                <div className="text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                  <p className="font-medium truncate">{videoFile.name}</p>
                  <p>
                    Size{' '}
                    {(videoFile.size / (1024 * 1024)).toFixed(2)}
                    {' '}MB
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-left text-gray-700 mb-3">
                    Quality <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={videoQuality}
                    onChange={(e) => setVideoQuality(e.target.value)}
                    disabled={isUploadingVideo}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
                  >
                    {VIDEO_QUALITY_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-left text-gray-700 mb-3">
                    Video Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={videoType}
                    onChange={(e) => setVideoType(e.target.value)}
                    disabled={isUploadingVideo}
                    className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
                  >
                    {VIDEO_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  className="w-4 h-4 text-[#EAB308] border-gray-300 rounded focus:ring-[#EAB308]"
                  checked={makeDefaultVideo}
                  onChange={(e) => setMakeDefaultVideo(e.target.checked)}
                  disabled={isUploadingVideo}
                />
                Set as default playback source
              </label>

              {uploadError && (
                <p className="text-sm text-red-600 text-left">{uploadError}</p>
              )}

              {(isUploadingVideo || uploadProgress > 0) && (
                <div className="space-y-2">
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-2 bg-[#EAB308] transition-all duration-200"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-sm text-gray-600 text-left">
                    {isUploadingVideo
                      ? uploadStatus === 'uploading'
                        ? `Uploading... ${uploadProgress}%`
                        : uploadStatus === 'initiating'
                        ? 'Preparing upload session...'
                        : uploadStatus === 'completing'
                        ? 'Finalising upload...'
                        : 'Processing...'
                      : uploadProgress === 100
                      ? 'Upload completed'
                      : `Progress: ${uploadProgress}%`}
                  </p>
                </div>
              )}

              <div className="flex items-center justify-end gap-3">
                <Button
                  type="button"
                  variant="primary"
                  onClick={handleVideoUpload}
                  disabled={!videoFile || isUploadingVideo}
                  isLoading={isUploadingVideo}
                >
                  Upload Video
                </Button>
                {videoFile && !isUploadingVideo && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={resetVideoUploadState}
                  >
                    Clear Selection
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-3 pt-4 border-t border-gray-200 mt-2">
          <h3 className="text-sm font-semibold text-gray-900 text-left">
            Existing Video Variants
          </h3>
          {renderVideoVariantsTable()}
        </div>

        <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 mt-4">
          <Button type="button" variant="outline" onClick={handleClose}>
            Close
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default EpisodeVideoUploadModal;


